import React, { useEffect, useState } from "react";
import {
  Checkbox,
  message,
  Modal,
  Radio,
  Select,
  Space,
  Typography,
} from "antd";
import "../../../crmSharedStyle.css";

const { Text } = Typography;

const EMPTY_PERMISSIONS = {
  leads: {
    create: false,
    read: false,
    update: false,
    notes: false,
  },
};

const EditPermissionsModal = ({
  open,
  apiKey,
  onClose,
  onSave,
  users = [],
}) => {
  const [permissions, setPermissions] =
    useState(EMPTY_PERMISSIONS);
  const [defaultAssignedTo, setDefaultAssignedTo] = useState(null);
  const [assignmentMode, setAssignmentMode] = useState("specific");
  const [assignmentPool, setAssignmentPool] = useState([]);

  useEffect(() => {
    if (!open || !apiKey) return;

    let current = apiKey.permissions || {};

    if (typeof current === "string") {
      try {
        current = JSON.parse(current);
      } catch {
        current = {};
      }
    }

    setPermissions({
      leads: {
        create:
          !!current?.leads?.create,

        read:
          !!current?.leads?.read,

        update:
          !!current?.leads?.update,

        notes:
          !!current?.leads?.notes,
      },
    });

    setDefaultAssignedTo(apiKey.default_assigned_to || null);
    setAssignmentMode(apiKey.assignment_mode || "specific");
    setAssignmentPool(
      Array.isArray(apiKey.assignment_pool) ? apiKey.assignment_pool : []
    );
  }, [open, apiKey]);

  const changePermission = (
    action,
    checked
  ) => {
    setPermissions((prev) => ({
      ...prev,

      leads: {
        ...prev.leads,

        [action]: checked,
      },
    }));
  };

  return (
    <Modal
      title="Edit API Permissions"
      open={open}
      onCancel={onClose}
      onOk={() => {
        if (assignmentMode === "round_robin" && !assignmentPool.length) {
          message.error("Select at least one person to rotate between");
          return;
        }

        onSave({
          permissions,
          assignment_mode: assignmentMode,
          default_assigned_to: defaultAssignedTo,
          assignment_pool: assignmentPool,
        });
      }}
      okText="Save Changes"
      className="crm-modal-responsive"
    >
      <div style={{ marginTop: 16 }}>
        <Text type="secondary">
          Integration
        </Text>

        <div
          style={{
            fontWeight: 600,
            marginBottom: 20,
          }}
        >
          {apiKey?.name || "API Integration"}
        </div>

        <Text strong>Lead Assignment</Text>
        <div
          style={{
            fontSize: 12,
            color: "#888",
            marginBottom: 8,
          }}
        >
          How leads created through this key are assigned when the request
          doesn't specify an assignee.
        </div>
        <Radio.Group
          value={assignmentMode}
          onChange={(e) => setAssignmentMode(e.target.value)}
          style={{ marginBottom: 16 }}
        >
          <Space direction="vertical">
            <Radio value="specific">One person</Radio>
            <Radio value="round_robin">Round robin (rotate through selected people)</Radio>
            <Radio value="workload">Auto (workload balanced across all active agents)</Radio>
          </Space>
        </Radio.Group>

        {assignmentMode === "specific" && (
          <>
            <div
              style={{
                fontSize: 12,
                color: "#888",
                marginBottom: 8,
              }}
            >
              Leave blank to default to whoever generated this key
              {apiKey?.created_by_name ? ` (${apiKey.created_by_name})` : ""}.
            </div>
            <Select
              style={{ width: "100%", marginBottom: 20 }}
              placeholder="Whoever generates this key (default)"
              allowClear
              showSearch
              optionFilterProp="label"
              value={defaultAssignedTo}
              onChange={setDefaultAssignedTo}
              options={users.map((u) => ({ label: u.full_name, value: u.id }))}
            />
          </>
        )}

        {assignmentMode === "round_robin" && (
          <>
            <div
              style={{
                fontSize: 12,
                color: "#888",
                marginBottom: 8,
              }}
            >
              Each new lead goes to the next person in this list, in order,
              looping back to the start.
            </div>
            <Select
              mode="multiple"
              style={{ width: "100%", marginBottom: 20 }}
              placeholder="Select agents to rotate between"
              showSearch
              optionFilterProp="label"
              value={assignmentPool}
              onChange={setAssignmentPool}
              options={users.map((u) => ({ label: u.full_name, value: u.id }))}
            />
          </>
        )}

        {assignmentMode === "workload" && (
          <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
            Each lead goes to whoever among your active agents currently has
            the lightest load.
          </Text>
        )}

        <Text strong>Lead Permissions</Text>

        <Space
          direction="vertical"
          style={{
            width: "100%",
            marginTop: 12,
          }}
        >
          <Checkbox
            checked={
              permissions.leads.create
            }
            onChange={(e) =>
              changePermission(
                "create",
                e.target.checked
              )
            }
          >
            Create Leads
          </Checkbox>

          <Checkbox
            checked={
              permissions.leads.read
            }
            onChange={(e) =>
              changePermission(
                "read",
                e.target.checked
              )
            }
          >
            Read Lead Details
          </Checkbox>

          <Checkbox
            checked={
              permissions.leads.update
            }
            onChange={(e) =>
              changePermission(
                "update",
                e.target.checked
              )
            }
          >
            Update Leads
          </Checkbox>

          <Checkbox
            checked={
              permissions.leads.notes
            }
            onChange={(e) =>
              changePermission(
                "notes",
                e.target.checked
              )
            }
          >
            Add Lead Notes
          </Checkbox>
        </Space>
      </div>
    </Modal>
  );
};

export default EditPermissionsModal;